import { faComments, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	ChatConnectionConfig,
	ChatMessageView,
	ChatParticipantView,
} from '@supply-mover-2-0/web-ui';
import {
	Avatar,
	ChatThread,
	parsePrincipalId,
	useChatConnection,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { refreshAccessToken } from '../lib/portalTokenService';
import {
	getChatContext,
	getChatIdentity,
	getCurrentChat,
	listMessages,
	resolveChat,
	startChat,
	type PortalChatAssignedRep,
	type PortalChatContextDto,
	type PortalChatIdentity,
	type PortalChatMessageDto,
	type PortalChatParticipantDto,
} from '../services/portalChatService';
import { useChatLauncherStore } from '../stores/chatLauncherStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/**
 * Customer-support help widget for the ecommerce portal. Floats in
 * the bottom-right, mirrors the shape of Intercom / Drift / similar
 * tools: closed pill → intake form → waiting → live chat.
 *
 * Portal-first: shows the caller's assigned sales rep up front (if
 * any) so the customer knows who they'll be connected with. If that
 * rep is online, the startCustomerChat flow server-side claims them
 * immediately — otherwise the chat sits in the pool and any rep can
 * pick it up.
 *
 * MQTT push via the shared `useChatConnection` hook — the SAME
 * connection lifecycle (backoff, terminal-auth self-heal, Last Will,
 * re-subscribe on reconnect) the CRM's ChatProvider uses, so the two
 * clients can't drift. The hook dynamic-imports mqtt.js on first
 * connect, so visitors who never open the widget past intake don't
 * pay the bundle cost. The same broker the CRM uses also serves
 * portal principals — the IoT authorizer maps the portal token to a
 * `portal:{tenant}:...` principalId and grants access to that single
 * user's inbox/outbox topics, so we get realtime delivery without a
 * parallel infrastructure stack.
 *
 * Catch-up uses REST: on every (re)connect we pull any messages
 * newer than the latest one we have via `listMessages?afterMessageId=`,
 * so a brief network blip never loses messages from the transcript.
 */

// REST catch-up window cap. We page if a reconnect catches a flood
// (unlikely on a customer-support chat; this is mostly defensive).
const CATCHUP_LIMIT = 200;

// While the chat panel is open, how often it re-asks whether anyone on the
// team is available (PD-15101). `teamAvailable` is otherwise read only when
// the chat is launched, so a customer who opened the panel while the team
// was offline kept seeing "Team is offline" after a rep came online.
const TEAM_AVAILABILITY_RECHECK_MS = 60_000;
// Focus and visibilitychange both fire when the customer returns to the
// tab; this keeps that from becoming two back-to-back requests.
const TEAM_AVAILABILITY_MIN_GAP_MS = 5_000;

type WidgetStage =
	| {
			// Initial / pre-bootstrap state. Launcher renders nothing
			// until /context resolves so we don't flash a button that
			// might disappear a moment later when we learn the user
			// isn't eligible. Distinct from `closed` so the launcher's
			// render gate can differentiate "we know you can chat,
			// just haven't opened it" from "we don't know yet."
			kind: 'loading';
	  }
	| { kind: 'closed' }
	| { kind: 'unavailable' }
	| {
			// Transient state while we POST /start. Renders the panel
			// chrome with a small spinner so the user gets immediate
			// feedback even if start-chat takes a beat.
			kind: 'starting';
			context: Extract<PortalChatContextDto, { available: true }>;
	  }
	| {
			kind: 'live';
			conversationId: number;
			waiting: boolean;
			context: Extract<PortalChatContextDto, { available: true }> | null;
	  };

/**
 * Apply a freshly read `teamAvailable` to an open panel's stage. Returns the
 * same object when nothing changed so React skips the re-render; any stage
 * other than an open panel is left alone (the panel may have closed while
 * the read was in flight).
 */
function withTeamAvailable(
	stage: WidgetStage,
	ctx: Extract<PortalChatContextDto, { available: true }>,
): WidgetStage {
	if (stage.kind !== 'starting' && stage.kind !== 'live') {
		return stage;
	}
	if (stage.context == null) {
		return { ...stage, context: ctx };
	}
	if (stage.context.teamAvailable === ctx.teamAvailable) {
		return stage;
	}
	return {
		...stage,
		context: { ...stage.context, teamAvailable: ctx.teamAvailable },
	};
}

export const HelpChatWidget: React.FC = () => {
	const { t } = useTranslation();
	const token = usePortalAuthStore((s) => s.token);
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	// Portal-level chat opt-in (PD-14608): every chat surface stays hidden
	// unless portal_site.settings.allowChat is on. Read before any effect
	// so a disabled portal never even bootstraps the chat context.
	const chatEnabled = useDisplaySettings().canUseChat;
	const openRequestVersion = useChatLauncherStore((s) => s.openRequestVersion);
	const setHasActiveConversation = useChatLauncherStore(
		(s) => s.setHasActiveConversation,
	);
	const clearUnread = useChatLauncherStore((s) => s.clearUnread);
	const resetLauncher = useChatLauncherStore((s) => s.reset);
	// Start in `loading` so the launcher renders nothing until the
	// bootstrap fetch finishes. Without this, the launcher flashes
	// visible for ~200ms then disappears for users whose /context
	// returns `available: false` — confusing UX that looks like a bug.
	const [stage, setStage] = useState<WidgetStage>({ kind: 'loading' });
	const [startError, setStartError] = useState<string | null>(null);

	// Mount-time bootstrap: figure out if we should show anything at
	// all, and if there's an in-flight chat already. Skipped until the
	// user is authenticated — anonymous visitors don't get the widget
	// yet (no known client → no sensible rep routing).
	//
	// IMPORTANT: we deliberately do NOT auto-open the panel when an
	// existing conversation is found. Auto-opening hijacked the screen
	// on every page load (especially harsh on mobile, where the panel
	// goes full-screen) and made the user feel pursued by the chat
	// rather than in control of it. Instead, we surface the
	// `hasActiveConversation` flag to `chatLauncherStore` so the
	// BottomNav's Chat tab + desktop FAB can render an "active" dot
	// — the user taps when they're ready to resume.
	useEffect(() => {
		if (!isAuthenticated || token == null || !chatEnabled) {
			// Anonymous or chat-disabled portal: hide the launcher
			// entirely. `loading` reads the same as `unavailable` at the
			// render gate (both hide), but keeping it distinct preserves
			// the "haven't checked yet" semantic in case the user
			// authenticates without remounting.
			setStage({ kind: 'loading' });
			resetLauncher();
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const [ctx, cur] = await Promise.all([
					getChatContext(token),
					getCurrentChat(token),
				]);
				if (cancelled) return;
				if (!ctx.available) {
					setStage({ kind: 'unavailable' });
					setHasActiveConversation(false);
					return;
				}
				setHasActiveConversation(cur.conversation != null);
				// Either way, panel stays closed. The user opens it via
				// the BottomNav Chat tab (mobile) or FAB (desktop+).
				setStage({ kind: 'closed' });
			} catch {
				if (!cancelled) {
					setStage({ kind: 'unavailable' });
					setHasActiveConversation(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [
		isAuthenticated,
		token,
		chatEnabled,
		setHasActiveConversation,
		resetLauncher,
	]);

	// Clear the unread badge whenever the panel transitions to a
	// foreground state. The user is now actively reading the
	// conversation, so any incoming messages from this point are seen
	// in real time and don't belong in the unread bucket.
	useEffect(() => {
		if (stage.kind === 'live' || stage.kind === 'starting') {
			clearUnread();
		}
	}, [stage.kind, clearUnread]);

	// External open requests (e.g. the BottomNav's Chat tab on mobile, or
	// any future "Talk to a rep" CTA) bump
	// `useChatLauncherStore.openRequestVersion`. We watch that and run the
	// same launchChat flow the FAB would. Skipping the initial mount value
	// (0) keeps us from auto-opening on first render.
	useEffect(() => {
		if (openRequestVersion === 0) return;
		if (!isAuthenticated || token == null) return;
		// Don't try to open while bootstrap is still resolving or
		// when the bootstrap said this user can't chat. The bottom-
		// nav Chat tab won't render in those states anyway, but the
		// guard is defensive — `requestOpen()` could fire from
		// future surfaces that don't gate on launcher visibility.
		if (stage.kind === 'loading' || stage.kind === 'unavailable') return;
		// Already open — leave it; reopening would reset scroll/state.
		if (stage.kind === 'live' || stage.kind === 'starting') return;
		void launchChat();
		// launchChat is defined below in component scope; effect only fires
		// on version bump so a missing dep doesn't matter for correctness.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openRequestVersion]);

	// Keep the panel's "Team is offline" line current while it is open:
	// re-check on a slow interval and whenever the customer comes back to
	// the tab, and stop as soon as the panel closes. Only `teamAvailable`
	// is taken from the fresh context. It is still the tenant-wide
	// aggregate, so no individual rep's status reaches the storefront.
	const panelOpen = stage.kind === 'starting' || stage.kind === 'live';
	useEffect(() => {
		if (!panelOpen || token == null) {
			return;
		}
		let cancelled = false;
		// The panel opened on a context fetched moments ago by launchChat.
		let lastCheckAt = Date.now();
		const recheck = async (): Promise<void> => {
			if (Date.now() - lastCheckAt < TEAM_AVAILABILITY_MIN_GAP_MS) {
				return;
			}
			lastCheckAt = Date.now();
			try {
				const ctx = await getChatContext(token);
				if (cancelled || !ctx.available) {
					return;
				}
				setStage((prev) => withTeamAvailable(prev, ctx));
			} catch {
				// Keep the last known answer; the next tick or focus retries.
			}
		};
		const intervalId = window.setInterval(() => {
			void recheck();
		}, TEAM_AVAILABILITY_RECHECK_MS);
		const onFocus = (): void => {
			void recheck();
		};
		const onVisibilityChange = (): void => {
			if (document.visibilityState === 'visible') {
				void recheck();
			}
		};
		window.addEventListener('focus', onFocus);
		document.addEventListener('visibilitychange', onVisibilityChange);
		return (): void => {
			cancelled = true;
			window.clearInterval(intervalId);
			window.removeEventListener('focus', onFocus);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [panelOpen, token]);

	// Hide the launcher when:
	//   - the user isn't signed in (anonymous shoppers don't get
	//     customer-support chat — no client to route against);
	//   - the bootstrap fetch hasn't resolved yet (`loading`) — avoids
	//     flashing the button visible then hiding it once we learn
	//     `ctx.available === false`;
	//   - the bootstrap definitively says this user can't chat
	//     (`unavailable` — no linked client, no tenant, etc).
	if (
		!isAuthenticated ||
		!chatEnabled ||
		stage.kind === 'loading' ||
		stage.kind === 'unavailable'
	) {
		return null;
	}

	// Single-click open: clicking the launcher resumes an in-flight
	// chat OR creates a fresh one immediately. No topic prompt — the
	// rep can ask "what's up?" once they're connected; making the
	// customer fill out a form before they can talk to a human is
	// exactly the friction we're trying to avoid.
	const launchChat = async (): Promise<void> => {
		if (token == null) return;
		setStartError(null);
		try {
			const ctx = await getChatContext(token);
			if (!ctx.available) {
				setStage({ kind: 'unavailable' });
				setHasActiveConversation(false);
				return;
			}
			const cur = await getCurrentChat(token);
			if (cur.conversation != null) {
				// Already had a chat in flight — resume it directly.
				setStage({
					kind: 'live',
					conversationId: cur.conversation.id,
					waiting: false,
					context: ctx,
				});
				setHasActiveConversation(true);
				return;
			}
			// Render the panel chrome immediately with a starting
			// spinner so the user sees feedback while we POST /start.
			setStage({ kind: 'starting', context: ctx });
			const r = await startChat(token, {});
			setStage({
				kind: 'live',
				conversationId: r.conversationId,
				waiting: !r.immediatelyAssigned,
				context: ctx,
			});
			setHasActiveConversation(true);
		} catch (e: unknown) {
			setStartError(e instanceof Error ? e.message : 'Could not start chat');
			// Fall back to closed so the user can retry by clicking
			// again. The error message is surfaced inside the
			// launcher's tooltip path on next attempt.
			setStage({ kind: 'closed' });
		}
	};

	// The assigned rep is the highest-value piece of context we have
	// pre-conversation — name it explicitly so the panel header can
	// humanize the chrome with the rep's identity. Falls back to null
	// when /context hasn't resolved yet OR the caller has no assigned
	// rep configured (chat lands in the unassigned pool).
	//
	// Per the locked availability design memory, we DO NOT see this
	// rep's individual status — `teamAvailable` is the only signal
	// the panel uses to set customer expectations, and it's a tenant
	// aggregate (was anyone able to take the chat?). The assigned
	// rep's identity stays for relationship preservation; their
	// schedule does not leak.
	const rep =
		stage.kind === 'starting' || stage.kind === 'live'
			? (stage.context?.assignedRep ?? null)
			: null;
	const teamAvailable =
		(stage.kind === 'starting' || stage.kind === 'live') &&
		stage.context?.teamAvailable === true;

	return (
		<>
			{stage.kind === 'closed' ? (
				<ChatLauncher
					onClick={() => void launchChat()}
					errorHint={startError}
				/>
			) : (
				<ChatPanel
					rep={rep}
					teamAvailable={teamAvailable}
					fallbackTitle={t('Chat with us')}
					onClose={() => setStage({ kind: 'closed' })}
				>
					{stage.kind === 'starting' && <StartingBody rep={rep} />}
					{stage.kind === 'live' && token != null && (
						<LiveBody
							token={token}
							conversationId={stage.conversationId}
							initialWaiting={stage.waiting}
							onResolved={() => {
								// Conversation reached its terminal state
								// (rep ended OR portal user clicked End chat
								// OR a server-emitted ended-system message
								// landed). Clear the launcher's "active"
								// badge so the dot doesn't keep nudging the
								// user back into a wrapped conversation.
								//
								// We deliberately DO NOT close the panel
								// here — the user is presumably reading the
								// final messages or the ended bar, and
								// yanking the window out from under them
								// would be hostile. They close it via the
								// X button when they're done.
								setHasActiveConversation(false);
							}}
						/>
					)}
				</ChatPanel>
			)}
		</>
	);
};

const ChatLauncher: React.FC<{
	onClick: () => void;
	errorHint: string | null;
}> = ({ onClick, errorHint }) => {
	const { t } = useTranslation();
	const hasActiveChat = useChatLauncherStore((s) => s.hasActiveConversation);
	const unreadCount = useChatLauncherStore((s) => s.unreadCount);
	const badgeLabel = unreadCount >= 10 ? '9+' : unreadCount;
	return (
		// Desktop-only floating launcher. On mobile the chat is reachable
		// from the BottomNav's "Chat" tab — having the FAB and the tab
		// together would be redundant and the FAB ate the corner the user
		// expects to scroll to. `hidden md:flex` keeps the launcher for
		// tablet+ where there's no bottom nav.
		<button
			type="button"
			onClick={onClick}
			className="fixed bottom-6 right-6 z-50 hidden md:flex h-14 w-14 items-center justify-center rounded-full bg-portal-accent text-white shadow-lg transition-transform hover:scale-105"
			aria-label={t('Chat with us')}
			title={errorHint != null ? t(errorHint) : t('Chat with us')}
		>
			<FontAwesomeIcon icon={faComments} className="h-5 w-5" />
			{unreadCount > 0 ? (
				<span className="absolute -top-1 -right-1 bg-portal-cart text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ring-2 ring-white dark:ring-success">
					{badgeLabel}
				</span>
			) : hasActiveChat ? (
				<span
					className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-portal-accent ring-2 ring-white dark:ring-success"
					aria-hidden="true"
				/>
			) : null}
		</button>
	);
};

const ChatPanel: React.FC<{
	/**
	 * Assigned rep, when known. Drives the personalized leading —
	 * name + Avatar — instead of the generic comments icon. Identity
	 * only, never status (per design memory; per-rep availability
	 * doesn't leak to customers).
	 */
	rep: PortalChatAssignedRep | null;
	/**
	 * Tenant-wide aggregate from `/context`. True when at least one
	 * customer-facing rep is currently available. When false we show
	 * an honest "Team is offline — we'll reply when we're back"
	 * subtitle so the customer isn't sitting in a chat expecting
	 * an immediate response. When true we show NO subtitle —
	 * we deliberately don't promise response times because we
	 * have no real data to back that up; a fake "usually replies
	 * within X minutes" line is worse than silence.
	 *
	 * Chat creation is permitted in BOTH cases — the boolean controls
	 * expectation-setting, not the launcher. (Intercom/Zendesk/
	 * LiveChat convergent: never block the customer from sending.)
	 */
	teamAvailable: boolean;
	/** Header copy when no rep is assigned (unassigned pool). */
	fallbackTitle: string;
	onClose: () => void;
	children: React.ReactNode;
}> = ({ rep, teamAvailable, fallbackTitle, onClose, children }) => {
	const { t } = useTranslation();
	// Two presentations from one component:
	//   - Mobile (<md): full-screen overlay. The previous bottom-anchored
	//     "card with catalog peek" wasted ~30% of vertical space on
	//     content the user couldn't actually use, and gave us three
	//     overlapping close affordances (Back chevron of the page below,
	//     X in the panel header, "End chat" link at the bottom).
	//     Edge-to-edge with safe-area insets at top/bottom keeps the
	//     keyboard from squeezing the message list, and one X is the
	//     only dismiss control.
	//   - md+ (tablet/desktop): keeps the existing right-anchored
	//     floating panel — Intercom/Drift pattern, lets desktop power
	//     users keep the catalog visible alongside the conversation.
	return (
		// Themed via brand-* tokens so the panel inherits the tenant's
		// storefront colors (a brand teal, etc.) instead of slamming a
		// stark `bg-white` (light) / `bg-tooltip` (dark) panel onto a
		// brand-tinted page. Tokens map to CSS vars set per-tenant in
		// PortalThemeProvider — see `apps/ecommerce-web/src/index.css`.
		// Text remains gray-900 / gray-100 because there is no canonical
		// `text-brand-fg` token; the gray pair is high-contrast on every
		// supported brand-surface configuration we ship today.
		<div className="fixed inset-0 z-50 flex flex-col bg-brand-surface shadow-2xl md:inset-auto md:bottom-6 md:right-6 md:h-[32rem] md:max-h-[calc(100vh-7rem)] md:w-96 md:rounded-xl">
			<header className="flex items-center justify-between gap-2 border-b border-brand-border px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] md:pt-3">
				<div className="flex min-w-0 flex-1 items-center gap-2.5">
					{rep != null ? (
						// Identity-only Avatar — `online` prop deliberately
						// omitted. The customer sees the assigned rep's name
						// and face but never their schedule; team availability
						// is communicated via the subtitle below using the
						// tenant-aggregate `teamAvailable` boolean.
						<Avatar seed={`u:${rep.userId}`} name={rep.displayName} size="sm" />
					) : (
						<div className="flex h-7 w-7 items-center justify-center rounded-full bg-portal-accent text-white">
							<FontAwesomeIcon icon={faComments} className="h-3.5 w-3.5" />
						</div>
					)}
					<div className="min-w-0">
						<div className="truncate text-sm font-semibold text-default">
							{rep != null ? t(rep.displayName) : fallbackTitle}
						</div>
						{/*
						 * Subtitle is the honest-offline notice ONLY. We
						 * intentionally render nothing when the team is
						 * available — we have no real response-time data,
						 * and a fake "replies within X minutes" line is
						 * worse than silence.
						 */}
						{!teamAvailable && (
							<div className="truncate text-[11px] text-brand-muted">
								{t("Team is offline — we'll reply when we're back")}
							</div>
						)}
					</div>
				</div>
				<button
					type="button"
					onClick={onClose}
					className="-mr-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-brand-muted hover:bg-brand-input hover:text-strong"
					aria-label={t('Close')}
				>
					<FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
				</button>
			</header>
			{/* Body fills available height; footer (composer) lives inside
			    `children` and pins to the bottom via flex on this wrapper.
			    `pb-safe-bottom` keeps the composer above the iOS home
			    indicator without doubling padding on tablets/desktop. */}
			<div className="flex flex-1 flex-col overflow-hidden pb-safe-bottom md:pb-0">
				{children}
			</div>
		</div>
	);
};

/**
 * Spinner stub shown for the brief moment between the launcher click
 * and `/start` returning. Renders in the same panel chrome as
 * LiveBody so the transition is visually seamless — the user sees
 * the chat panel open, a skeleton, then their conversation.
 *
 * When an `assignedRep` is known we surface the rep card as a
 * secondary line so the user already sees who they're being routed
 * to BEFORE the conversation pane has painted. Without it we fall
 * back to the generic "team member" copy.
 */
const StartingBody: React.FC<{ rep: PortalChatAssignedRep | null }> = ({
	rep,
}) => {
	const { t } = useTranslation();
	return (
		<div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
			<div className="h-6 w-6 animate-spin rounded-full border-2 border-portal-accent border-t-transparent" />
			<div className="text-sm text-brand-muted">
				{rep != null
					? t('Setting up your chat…')
					: t('Connecting you to a team member…')}
			</div>
			{rep != null && (
				<div className="mt-1 flex items-center gap-2 text-xs text-muted">
					<Avatar seed={`u:${rep.userId}`} name={rep.displayName} size="sm" />
					<span>
						{t('with')} <strong>{t(rep.displayName)}</strong>
					</span>
				</div>
			)}
		</div>
	);
};

const LiveBody: React.FC<{
	token: string;
	conversationId: number;
	initialWaiting: boolean;
	onResolved: () => void;
}> = ({ token, conversationId, initialWaiting, onResolved }) => {
	const { t } = useTranslation();
	const [messages, setMessages] = useState<Array<ChatMessageView>>([]);
	const [participants, setParticipants] = useState<Array<ChatParticipantView>>(
		[],
	);
	const [sendErrors, setSendErrors] = useState<Record<string, string>>({});
	const [resolving, setResolving] = useState(false);
	// The identity bootstrap (`getChatIdentity`) result: server-built
	// principalId + topics + broker URL. The MQTT connection itself is
	// owned by the shared `useChatConnection` hook below — the same
	// lifecycle the CRM ChatProvider uses (lazy mqtt.js import, backoff,
	// terminal-auth self-heal, Last Will, re-subscribe on reconnect).
	// There's no REST fallback for sends — one path, one failure mode.
	const [identity, setIdentity] = useState<PortalChatIdentity | null>(null);
	const [identityFailed, setIdentityFailed] = useState(false);
	// The portal user's own workdUserId, parsed out of the principalId
	// returned by `getChatIdentity`. Used as the identity primitive for
	// the "is this mine?" decision in ChatThread — a single numeric
	// compare with no dependency on the conversation's participants
	// list. Without this, the optimistic bubble landed left-aligned
	// (or as a centered system pill) on a freshly-opened chat until
	// the participants REST fetch returned.
	const [myWorkdUserId, setMyWorkdUserId] = useState<number | undefined>(
		undefined,
	);
	// Boot state — true once the initial REST fetch has resolved (or
	// errored). Until then we show a unified loading panel instead of
	// flashing the "We're finding someone…" banner over an empty
	// transcript, which made the panel look like it was already in
	// the waiting state before we'd even had a chance to confirm
	// participants.
	const [booted, setBooted] = useState(false);
	// Sticky resolved state — set when EITHER the portal user clicks
	// "End chat" OR a server-emitted "ended" system message lands.
	// Once true the composer is permanently disabled but the
	// transcript stays visible so the user can scroll back through
	// the conversation; the launcher's close button is the only way
	// to hide the panel.
	const [chatEnded, setChatEnded] = useState(false);
	// Highest server message id we've seen — used as the watermark for
	// REST catch-up on (re)connect so we don't refetch the entire
	// transcript every time the broker briefly drops us.
	const lastServerMessageIdRef = useRef<number>(0);
	// Latest token, kept in a ref for the REST helpers (catchUp /
	// fetchParticipants / resolve) so they always send the current
	// token without appearing in effect dependency arrays. MQTT-side
	// token rotation is handled inside useChatConnection.
	const tokenRef = useRef(token);
	useEffect(() => {
		tokenRef.current = token;
	}, [token]);

	// Reset all per-conversation state BEFORE the load effects re-fire.
	// Declared first so the effect runs first (React runs effects in
	// declaration order on each render). Without this, the load effect
	// could land its REST response between the conversationId change
	// and the reset, briefly showing data from the OLD convo and then
	// immediately wiping it. The MQTT connection deliberately survives
	// a conversation change — it's identity-scoped, not conversation-
	// scoped, and the inbox handler filters by conversationId.
	useEffect(() => {
		setMessages([]);
		setParticipants([]);
		setSendErrors({});
		setBooted(false);
		setChatEnded(false);
		lastServerMessageIdRef.current = 0;
	}, [conversationId]);

	const mergeServerMessages = useCallback(
		(incoming: Array<PortalChatMessageDto>): void => {
			if (incoming.length === 0) return;
			setMessages((prev) => {
				// Bucket by `clientMessageId ?? uuid` so we both replace
				// optimistic rows AND no-op on duplicate MQTT deliveries.
				const byKey = new Map<string, ChatMessageView>();
				for (const m of prev) {
					byKey.set(m.clientMessageId ?? m.uuid, m);
				}
				for (const m of incoming) {
					const view = portalToMessageView(m);
					byKey.set(view.clientMessageId ?? view.uuid, view);
					if (view.id > 0 && view.id > lastServerMessageIdRef.current) {
						lastServerMessageIdRef.current = view.id;
					}
				}
				return Array.from(byKey.values()).sort((a, b) =>
					a.createdDate.localeCompare(b.createdDate),
				);
			});
		},
		[],
	);

	const fetchParticipants = useCallback(async (): Promise<void> => {
		try {
			// Reuse the existing /messages endpoint — it returns
			// participants alongside, so we don't need a dedicated
			// surface. limit=1 keeps the message page tiny since we
			// only want the participant side-effect here.
			const r = await listMessages(tokenRef.current, conversationId, {
				limit: 1,
			});
			setParticipants(r.participants.map(portalToParticipantView));
		} catch {
			/* transient — next event will trigger another fetch */
		}
	}, [conversationId]);

	const catchUp = useCallback(async (): Promise<void> => {
		try {
			// Page until a short batch: the server returns oldest-first for
			// afterMessageId, so each batch's max id is the next watermark.
			// A single fetch left a hole after any disconnect that missed
			// more than CATCHUP_LIMIT messages. Page cap bounds the loop; a
			// customer-support thread realistically never hits it.
			const MAX_CATCHUP_PAGES = 5;
			let participants: Array<PortalChatParticipantDto> | null = null;
			let watermark = lastServerMessageIdRef.current;
			for (let page = 0; page < MAX_CATCHUP_PAGES; page++) {
				const r = await listMessages(tokenRef.current, conversationId, {
					afterMessageId: watermark > 0 ? watermark : undefined,
					limit: CATCHUP_LIMIT,
				});
				mergeServerMessages(r.messages);
				participants = r.participants;
				if (r.messages.length < CATCHUP_LIMIT) break;
				watermark = r.messages.reduce(
					(acc, m) => (m.id > acc ? m.id : acc),
					watermark,
				);
			}
			// Always refresh participants on catch-up — membership may
			// have changed while we were offline.
			if (participants != null) {
				setParticipants(participants.map(portalToParticipantView));
			}
		} catch {
			/* network blip, next reconnect retries */
		}
	}, [conversationId, mergeServerMessages]);

	// Initial load → REST. Pulls the most recent page + participants.
	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const r = await listMessages(tokenRef.current, conversationId, {
					limit: 50,
				});
				if (cancelled) return;
				mergeServerMessages(r.messages);
				setParticipants(r.participants.map(portalToParticipantView));
			} catch {
				/* let MQTT catch-up backfill if/when it connects */
			} finally {
				// Flip booted regardless of success — even on a network
				// blip we'd rather show the empty transcript with the
				// waiting banner than leave the user staring at a
				// permanent loading spinner. MQTT catch-up will paint
				// any messages we missed once the broker connects.
				if (!cancelled) setBooted(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [conversationId, mergeServerMessages]);

	// Chat connection — shared lifecycle with the CRM (useChatConnection):
	// lazy mqtt.js import, exponential backoff, one-shot reactive token
	// refresh on terminal auth rejection, Last Will on the presence
	// topic, automatic re-subscribe after reconnect. The identity comes
	// from REST first: the server builds principalId + topics so the env
	// segment stays authoritative to whatever env crm-api is deployed in.
	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const id = await getChatIdentity(tokenRef.current);
				if (cancelled) return;
				setIdentity(id);
				// Parse our own workdUserId out of the principalId so
				// optimistic sends carry the identity primitive used by
				// ChatThread to right-align our own bubbles.
				const meParsed = parsePrincipalId(id.principalId);
				if (meParsed != null) setMyWorkdUserId(meParsed.workdUserId);
			} catch {
				if (!cancelled) setIdentityFailed(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const connectionConfig = useMemo<ChatConnectionConfig | null>(() => {
		if (identity == null) return null;
		return {
			wsUrl: identity.brokerUrl,
			principalId: identity.principalId,
			token,
			// One-shot reactive recovery on terminal CONNACK — cross-tab
			// coordinated via portalTokenService's TokenManager, so it
			// can't race the apiClient's 401 refresh.
			refreshToken: refreshAccessToken,
			// Last Will target: if the customer's tab drops while they're
			// waiting in the support queue, the server abandons the queue
			// entry so a rep never claims a ghost.
			presenceTopic: identity.presenceTopic,
		};
	}, [identity, token]);
	const connection = useChatConnection(connectionConfig);

	// Inbox dispatch. Ref-held so the subscription effect below never
	// has to re-subscribe when the handler's dependencies (current
	// conversation, merge callbacks) change — same pattern as the CRM
	// ChatProvider.
	const inboxHandlerRef = useRef<(payload: unknown) => void>(() => undefined);
	inboxHandlerRef.current = (payload: unknown) => {
		const parsed = payload as Record<string, unknown> | null;
		if (parsed == null || typeof parsed !== 'object') return;
		if (parsed.conversationId !== conversationId) return;
		if (parsed.type === 'message') {
			const id = parsed.messageId as number | undefined;
			const uuid = parsed.messageUuid as string | undefined;
			if (id == null || uuid == null) return;
			mergeServerMessages([
				{
					id,
					uuid,
					conversationId,
					senderParticipantId: parsed.senderParticipantId as number | undefined,
					senderWorkdUserId: parsed.senderWorkdUserId as number | undefined,
					clientMessageId: parsed.clientMessageId as string | undefined,
					kind: (parsed.kind as PortalChatMessageDto['kind']) ?? 'text',
					content: parsed.text as string,
					createdDate: new Date(
						(parsed.sentAt as number) ?? Date.now(),
					).toISOString(),
				},
			]);
		} else if (parsed.type === 'participants-changed') {
			void fetchParticipants();
		}
	};

	useEffect(() => {
		if (identity == null) return;
		return connection.subscribe(
			identity.inboxTopic,
			(_topic: string, payload: unknown) => inboxHandlerRef.current(payload),
		);
		// `connection` is deliberately not a dep: subscribe/unsubscribe act
		// on hook-internal refs that are stable across state re-renders,
		// and the hook re-subscribes registered filters itself after a
		// reconnect — re-running this effect on every state flip would
		// just churn UNSUBSCRIBE/SUBSCRIBE packets.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [identity]);

	// Catch up on any messages that arrived while the broker was down —
	// runs on every (re)connect transition, including the first, and
	// when the active conversation changes while connected.
	useEffect(() => {
		if (!connection.connected) return;
		void catchUp();
	}, [connection.connected, catchUp]);

	// Fast-path offline on tab close / navigation: pagehide fires before
	// the socket tears down, so we publish an explicit offline (a
	// graceful DISCONNECT would discard the Last Will). Deliberately a
	// window listener rather than the connection's cleanup — cleanup
	// also runs on identity/token-driven rebuilds, which would wrongly
	// abandon a still-waiting customer.
	useEffect(() => {
		if (identity == null) return;
		const presenceTopic = identity.presenceTopic;
		const onPageHide = (): void => {
			void connection
				.publish(presenceTopic, { at: Date.now(), offline: true })
				.catch(() => undefined);
		};
		window.addEventListener('pagehide', onPageHide);
		return () => {
			window.removeEventListener('pagehide', onPageHide);
		};
		// Same reasoning as the subscription effect: publish() reads
		// hook-internal refs, so the connection object identity is not a
		// meaningful dependency.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [identity]);

	/**
	 * A rep has joined the conversation if there's an `internal_user`
	 * participant row. Drives the waiting-vs-connected banner copy.
	 */
	const repJoined = useMemo(
		() =>
			participants.some(
				(p) => p.participantType === 'internal_user' && p.isActive,
			),
		[participants],
	);

	/**
	 * Publish a message envelope to the portal user's outbox topic.
	 * Mirrors the shape ChatProvider.sendText uses on the CRM side
	 * — same persistence subscriber consumes both, so the wire
	 * format MUST match exactly. The server then fans the saved
	 * message back out to every participant's inbox; this client
	 * receives its own confirmation via the inbox subscription and
	 * `mergeServerMessages` replaces the optimistic row keyed on
	 * `clientMessageId`.
	 */
	const publishOutbox = useCallback(
		async (clientMessageId: string, text: string): Promise<void> => {
			if (identity == null) {
				throw new Error('Chat connection not ready');
			}
			// connection.publish rejects when the socket isn't connected,
			// so the "not ready" failure mode surfaces as a send error on
			// the optimistic row — same contract as before.
			await connection.publish(identity.outboxTopic, {
				type: 'message',
				clientMessageId,
				conversationId,
				text,
			});
		},
		[connection, identity, conversationId],
	);

	const handleSend = useCallback(
		async (text: string): Promise<void> => {
			const clientMessageId = crypto.randomUUID();
			// Optimistic row — replaced when the server-confirmed copy
			// lands via the MQTT inbox fanout (server saves the
			// message, then publishes back to every participant
			// including the sender). `senderWorkdUserId` is the
			// identity primitive ChatThread uses to right-align our
			// own bubbles, set from the parsed principalId. We don't
			// stamp `senderParticipantId` here — only the server knows
			// the conversation-scoped participant id, and the fanout
			// reply fills it in via clientMessageId dedup.
			const optimistic: ChatMessageView = {
				id: -Date.now(),
				uuid: clientMessageId,
				conversationId,
				senderWorkdUserId: myWorkdUserId,
				clientMessageId,
				kind: 'text',
				content: text,
				createdDate: new Date().toISOString(),
			};
			setMessages((prev) => [...prev, optimistic]);
			try {
				await publishOutbox(clientMessageId, text);
			} catch (err) {
				const msg = err instanceof Error ? err.message : 'Send failed';
				setSendErrors((prev) => ({ ...prev, [clientMessageId]: msg }));
			}
		},
		[conversationId, myWorkdUserId, publishOutbox],
	);

	const handleRetry = useCallback(
		(clientMessageId: string) => {
			const stored = messages.find(
				(m) => m.clientMessageId === clientMessageId,
			);
			if (stored == null || stored.content == null) return;
			setSendErrors((prev) => {
				const { [clientMessageId]: _, ...rest } = prev;
				return rest;
			});
			void publishOutbox(clientMessageId, stored.content).catch(
				(err: unknown) => {
					const msg = err instanceof Error ? err.message : 'Send failed';
					setSendErrors((prev) => ({ ...prev, [clientMessageId]: msg }));
				},
			);
		},
		[messages, publishOutbox],
	);

	async function handleResolve(): Promise<void> {
		setResolving(true);
		try {
			await resolveChat(tokenRef.current, conversationId);
			// Stay in the live panel — the user may want to scroll
			// back through the transcript. Composer locks via
			// `chatEnded`; the launcher's close button is the only
			// way out from here.
			setChatEnded(true);
		} finally {
			setResolving(false);
		}
	}

	// Server-side resolution detection. The routing service emits
	// "Customer ended the chat" / "Sales rep ended the chat" as system
	// messages with metadata.reason='resolved' (kind: 'system') just
	// before flipping the conversation to Resolved. We can't read the
	// conversation status directly from the portal endpoint, so the
	// system pill is our signal — once one lands we lock the composer
	// even if the rep resolved from their side and the portal user
	// never clicked End chat.
	useEffect(() => {
		if (chatEnded) return;
		const ended = messages.some(
			(m) =>
				m.senderParticipantId == null &&
				m.kind === 'system' &&
				typeof m.content === 'string' &&
				/ended the chat/i.test(m.content),
		);
		if (ended) setChatEnded(true);
	}, [messages, chatEnded]);

	// Fire onResolved exactly once when the conversation reaches its
	// terminal state, regardless of whether the local user clicked
	// End chat or a server-emitted system message ended it. The
	// parent uses this to clear the launcher's active-conversation
	// dot so the badge doesn't keep showing on a chat that's already
	// wrapped — the previous code only had this signal on the
	// explicit-resolve button path, so server-side ends left a
	// stale dot on the launcher even with the panel open.
	useEffect(() => {
		if (chatEnded) onResolved();
	}, [chatEnded, onResolved]);

	const waiting = initialWaiting && !repJoined && !chatEnded;

	// Boot state — keep the unified spinner up until the first REST
	// fetch resolves AND the realtime channel either connects or
	// definitively fails. Without this, the panel briefly flashes
	// the empty thread + "We're finding someone…" banner the
	// moment LiveBody mounts, even though we haven't talked to the
	// server yet.
	if (!booted) {
		return (
			<div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
				<div className="h-6 w-6 animate-spin rounded-full border-2 border-portal-accent border-t-transparent" />
				<div className="text-sm text-brand-muted">
					{t('Connecting you to a team member…')}
				</div>
			</div>
		);
	}

	// Hard-fail surface. If the realtime channel can't be
	// established (identity bootstrap failed, mqtt.js failed to load,
	// or the broker permanently rejected our credentials after the
	// one-shot token refresh), there's no REST fallback — we don't
	// fork the send path, so a working chat requires a working
	// channel. Show a clear, non-spinning error instead of a stuck
	// "sending…" composer that silently swallows every keystroke.
	// Connection-level failures get a Try again button (the hook's
	// retry() re-arms the backoff + auth refresh); an identity
	// bootstrap failure needs a page refresh — the effect only runs
	// on mount by design.
	if (identityFailed || connection.state === 'failed') {
		return (
			<div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
				<div className="text-sm font-semibold text-default">
					{t("We couldn't open a chat session right now")}
				</div>
				<div className="max-w-xs text-xs text-brand-muted">
					{t(
						'Please refresh the page and try again. If this keeps happening, contact support by email.',
					)}
				</div>
				{!identityFailed && (
					<button
						type="button"
						onClick={() => connection.retry()}
						className="text-xs font-medium text-portal-accent underline-offset-2 hover:underline"
					>
						{t('Try again')}
					</button>
				)}
			</div>
		);
	}

	// Composer locks for any of: waiting on rep, chat ended, or
	// MQTT not connected (transient drop). The portal user never
	// types into the void — every state has explicit feedback.
	// `waiting` and `chatEnded` go further and HIDE the composer
	// entirely (see composerHidden below); composerDisabled here
	// is for the transient reconnecting case where the composer is
	// still visible but greyed.
	const composerLocked = waiting || chatEnded || !connection.connected;
	const placeholder = !connection.connected
		? t('Reconnecting…')
		: t('Type a message…');

	return (
		<ChatThread
			density="compact"
			messages={messages}
			participants={participants}
			myWorkdUserId={myWorkdUserId}
			sendErrors={sendErrors}
			onSend={handleSend}
			onRetry={handleRetry}
			// Composer is greyed during transient locks (reconnecting)
			// but fully removed for the two terminal/blocking states
			// — waiting on a rep and chat ended — where a disabled
			// input adds noise without affording any action. In those
			// cases the bottom slot communicates the state via a bar.
			composerDisabled={composerLocked}
			composerHidden={chatEnded || waiting}
			placeholder={placeholder}
			composerFooter={
				chatEnded ? (
					// Closed-state bar in place of the composer. Same
					// pattern the CRM rep pane uses: bordered strip,
					// surface-tinted background, secondary-text body.
					// Sticks where the input used to be so users
					// don't lose orientation.
					<div className="border-t border-brand-border bg-brand-input px-3 py-2 text-xs text-muted">
						{t(
							'This chat has ended. Start a new chat anytime if you need more help.',
						)}
					</div>
				) : waiting ? (
					// Waiting-state bar mirrors the ended-state bar:
					// the composer is gone (typing into the void with
					// no rep on the other end was always a worse UI
					// than honest "we're working on it" copy), so this
					// strip carries both the status line and an
					// affordance to bail out.
					<div className="flex items-center justify-between gap-2 border-t border-brand-border bg-warning-badge px-3 py-2 text-xs text-warning">
						<span>{t('Waiting for a rep to connect…')}</span>
						<button
							type="button"
							onClick={() => void handleResolve()}
							disabled={resolving}
							className="font-medium text-warning underline-offset-2 hover:underline disabled:opacity-50"
						>
							{resolving ? t('Cancelling…') : t('Cancel')}
						</button>
					</div>
				) : (
					<div className="border-t border-brand-border px-3 py-1.5">
						<button
							type="button"
							onClick={() => void handleResolve()}
							disabled={resolving}
							className="text-xs text-faint hover:text-muted"
						>
							{resolving ? t('Ending…') : t('End chat')}
						</button>
					</div>
				)
			}
		/>
	);
};

/**
 * Normalize the portal DTO to the shared `ChatMessageView` shape
 * used by ChatThread. The fields overlap 1:1 today — this wrapper
 * exists so future server-side renames only touch one spot.
 */
function portalToMessageView(m: PortalChatMessageDto): ChatMessageView {
	return {
		id: m.id,
		uuid: m.uuid,
		conversationId: m.conversationId,
		senderParticipantId: m.senderParticipantId,
		// Identity primitive — the sender's workdUserId — used by
		// ChatThread to decide right-vs-left alignment without
		// needing the conversation's participants list to have loaded.
		// Populated by the backend `listHistory` enrichment for every
		// non-system message.
		senderWorkdUserId: m.senderWorkdUserId,
		// Must propagate so the optimistic row can be replaced by the
		// confirmed server copy; without this the same message renders
		// twice (floating optimistic + confirmed) until a full refresh.
		clientMessageId: m.clientMessageId,
		kind: m.kind,
		content: m.content,
		createdDate: m.createdDate,
	};
}

function portalToParticipantView(
	p: PortalChatParticipantDto,
): ChatParticipantView {
	return {
		id: p.id,
		conversationId: 0, // unused by ChatThread's renderer
		participantType:
			p.participantType as ChatParticipantView['participantType'],
		userId: p.userId,
		role: p.role as ChatParticipantView['role'],
		displayName: p.displayName,
		email: p.email,
		isActive: p.isActive,
		notificationsMuted: false,
	};
}
